/* global bocAmchart4, am4core, am4charts, chartLanguage */
/**
 * Get colors for the map from the shortcode or define defaults
 * @param {object} chart object
 * @param {object} fill am4core color passed from amcharts
 * @param {object} target from template adapter, source of data item
 * @returns {object} am4core color
 */
bocAmchart4.calculateHeatmapColors = function (chart, fill, target) {
    // Override markerCount with user-defined shortcode values
    let markerCount = 2;
    if (chart.bocSettings.args.hasOwnProperty('markercount')) {
        markerCount = parseFloat(chart.bocSettings.args.markercount);
    }

    const legendBuckets = bocAmchart4.vars[chart.bocSettings.id].legendBuckets;
    const minValue = parseFloat(bocAmchart4.vars[chart.bocSettings.id].minValue);
    const maxValue = parseFloat(bocAmchart4.vars[chart.bocSettings.id].maxValue);

    // Debug logging
    if (bocAmchart4.debug(chart)) {
        // eslint-disable-next-line no-console
        console.log(
            chart.bocSettings.id,
            "Using these min/max:",
            "Min value: " + minValue,
            "Max value: " + maxValue,
            "Marker count:" + markerCount
        );
    }

    const mainColors = bocAmchart4.vars[chart.bocSettings.id].mainColors;
    bocAmchart4.vars[chart.bocSettings.id].locale = (chartLanguage === "fr") ? 'fr-CA' : 'en-CA';

    // Check if legend has already been set up
    if (bocAmchart4.vars[chart.bocSettings.id].hasOwnProperty("maxLabel")) {
        bocAmchart4.vars[chart.bocSettings.id].maxLabel.text = bocAmchart4.locale(chart, maxValue);
    } else {
        setupHeatmapLegend(chart, markerCount, mainColors, legendBuckets);
    }

    // Calculate fill color for the target data point
    if (!isNaN(target.dataItem.value)) {
        let value = target.dataItem.value;
        let allColorsMap = bocAmchart4.vars[chart.bocSettings.id].allColors;
        let minOverflowBucket = allColorsMap[0].low;
        let maxOverflowBucket = allColorsMap[allColorsMap.length - 1].high;
        fill = bocAmchart4.getFillColor(value, allColorsMap, markerCount, minOverflowBucket, maxOverflowBucket);
    }

    return fill;
};

/**
 * Sets up the heatmap legend: includes the legend container, color rectangles, bucket labels and tooltips.
 * @param {object} chart - The amCharts chart instance.
 * @param {number} markerCount - The number of color buckets.
 * @param {object[]} mainColors - Array of main color objects.
 * @param {number[]} legendBuckets - Array of bucket values used for the heatmap color ranges.
 * @returns {void}
 */
function setupHeatmapLegend(chart, markerCount, mainColors, legendBuckets) {
    // Create heat legend container and size
    let heatLegend = chart.bottomAxesContainer.createChild(am4core.Container);
    heatLegend.width = am4core.percent(100);
    heatLegend.height = 20;
    heatLegend.align = "right";
    heatLegend.valign = "top";
    heatLegend.layout = "horizontal";

    // Create a container for the legend labels
    let legendLabelContainer = chart.bottomAxesContainer.createChild(am4core.Container);
    legendLabelContainer.layout = "horizontal";
    legendLabelContainer.valign = "top";
    legendLabelContainer.layout = "absolute";
    legendLabelContainer.width = am4core.percent(100);

    // Create rectangles and assign colors
    let rect = createColorRectangles(heatLegend, markerCount, mainColors, chart);

    // Assign low and high values to each colour
    assignColorBuckets(chart, legendBuckets);

    // Create min and max labels
    createLegendMinMaxLabels(chart, legendLabelContainer, rect, markerCount);

    // Set up tooltips for all rectangles
    setupLegendTooltips(chart, rect, markerCount);

    // Setup mid-labels if enabled
    setupMidLabels(chart, legendLabelContainer, rect, markerCount);
}

/**
 * Creates the color rectangles for the heatmap legend and assigns interpolated colors.
 * @param {object} heatLegend - The container for the heatmap legend rectangles.
 * @param {number} markerCount - The number of buckets in the legend.
 * @param {object[]} mainColors - Array of main color values to interpolate from.
 * @param {object} chart - The amCharts chart instance.
 * @returns {object[]} - Array of Rectangle objects representing the color buckets.
 */
function createColorRectangles(heatLegend, markerCount, mainColors, chart) {
    const rect = [];
    bocAmchart4.vars[chart.bocSettings.id].allColors = [];

    for (let index = 0; index < markerCount; index++) {
        // Create new rectangle for each bucket (based on markerCount)
        rect[index] = heatLegend.createChild(am4core.Rectangle);
        rect[index].width = am4core.percent(100);
        rect[index].height = 15;
        rect[index].strokeWidth = 0;

        let fill;
        if (index === 0) {
            // Assign first colour in mainColours to the first bucket in the legend
            fill = mainColors[index];
        } else if (index === markerCount - 1) {
            // Assign last colour in mainColours to the last bucket in the legend
            fill = mainColors[mainColors.length - 1];
        } else {
            // Find the next gradient colour
            const relValueLegend = (index / (markerCount - 1));
            const arrayRangeLegend = relValueLegend * (mainColors.length - 1);
            const baseIdxLegend = Math.floor(arrayRangeLegend);
            const gradientRangeLegend = arrayRangeLegend - baseIdxLegend;

            fill = am4core.colors.interpolate(
                mainColors[baseIdxLegend].rgb,
                mainColors[baseIdxLegend + 1].rgb,
                gradientRangeLegend
            );
        }

        // Add colour to allColors variable and to rectangle
        bocAmchart4.vars[chart.bocSettings.id].allColors[index] = {
            'color': fill
        };
        rect[index].fill = fill;
    }

    return rect;
}

/**
 * Assigns the numeric range for each color bucket based on legendBuckets.
 * @param {object} chart - The amCharts chart instance.
 * @param {number[]} legendBuckets - The numeric breakpoints for color buckets.
 * @returns {void}
 */
function assignColorBuckets(chart, legendBuckets) {
    const allColors = bocAmchart4.vars[chart.bocSettings.id].allColors;
    allColors[0].low = legendBuckets[0];
    allColors[0].high = legendBuckets[1];

    let index = 1;
    for (; index < legendBuckets.length - 1; index++) {
        allColors[index].low = legendBuckets[index];
        allColors[index].high = legendBuckets[index + 1] ?? false;
    }

    allColors[index - 1].high = legendBuckets[index];
}

/**
 * Creates and positions the minimum and maximum labels on the heatmap legend.
 * @param {object} chart - The amCharts chart instance.
 * @param {object} legendLabelContainer - Container for legend labels.
 * @param {object[]} rect - Array of color rectangles used in the legend.
 * @param {number} markerCount - Total number of color buckets.
 * @returns {void}
 */
function createLegendMinMaxLabels(chart, legendLabelContainer, rect, markerCount) {
    const allColors = bocAmchart4.vars[chart.bocSettings.id].allColors;

    // Create label container for minimum bucket
    const legendLabelMin = legendLabelContainer.createChild(am4core.Label);
    legendLabelMin.fontSize = "0.875em";
    legendLabelMin.valign = "top";

    // Sets up tooltips/labels based on whether min overflow bucket exists
    if (allColors[0].low === false) {
        // Overflow bucket exists so align the label with the first divider
        legendLabelMin.text = bocAmchart4.locale(chart, allColors[0].high);
        legendLabelMin.opacity = 1;
        legendLabelMin.validate();
        positionLabelBetweenRects(legendLabelMin, rect[0], rect[1]);
    } else {
        legendLabelMin.align = "left";
        legendLabelMin.horizontalCenter = "middle";
        legendLabelMin.text = bocAmchart4.locale(chart, allColors[0].low);
    }
    bocAmchart4.vars[chart.bocSettings.id].minLabel = legendLabelMin;

    // Create label container for maximum bucket
    const legendLabelMax = legendLabelContainer.createChild(am4core.Label);
    legendLabelMax.fontSize = "0.875em";
    legendLabelMax.valign = "top";

    // Sets up tooltips/labels based on whether max overflow bucket exists
    if (allColors[allColors.length - 1].high === false) {
        // Overflow bucket exists so align the label with the last divider
        legendLabelMax.text = bocAmchart4.locale(chart, allColors[allColors.length - 1].low);
        legendLabelMax.opacity = 1;

        // Force label to validate and calculate its dimension
        legendLabelMax.validate();
        positionLabelBetweenRects(legendLabelMax, rect[markerCount - 2], rect[markerCount - 1]);
    } else {
        legendLabelMax.align = "right";
        legendLabelMax.horizontalCenter = "middle";
        legendLabelMax.text = bocAmchart4.locale(chart, allColors[allColors.length - 1].high);
    }
    bocAmchart4.vars[chart.bocSettings.id].maxLabel = legendLabelMax;

    /**
     * Repositions the min and max labels on the heatmap legend after layout validation.
     * This is needed when overflow buckets are present, as their labels
     * need to be manually aligned between the appropriate color rectangles.
     * @returns {void}
     */
    function positionLabels() {
        if (allColors[0].low === false) {
            legendLabelMin.validate();
            positionLabelBetweenRects(legendLabelMin, rect[0], rect[1]);
        }
        if (allColors[allColors.length - 1].high === false) {
            legendLabelMax.validate();
            positionLabelBetweenRects(legendLabelMax, rect[markerCount - 2], rect[markerCount - 1]);
        }
    }
    chart.events.on("layoutvalidated", () => {
        positionLabels();
    });
}

/**
 * Sets up tooltips for each rectangle in the heatmap legend.
 * @param {object} chart - The amCharts chart instance.
 * @param {object[]} rect - Array of rectangle elements representing color buckets.
 * @param {number} markerCount - The number of color buckets in the legend.
 * @returns {void}
 */
function setupLegendTooltips(chart, rect, markerCount) {
    const allColors = bocAmchart4.vars[chart.bocSettings.id].allColors;

    // Tooltip for the first bucket
    if (allColors[0].low === false) {
        rect[0].tooltipText = "< " + bocAmchart4.locale(chart, allColors[0].high);
    } else {
        rect[0].tooltipText = "≥ " + bocAmchart4.locale(chart, allColors[0].low) + "\n"
            + "< " + bocAmchart4.locale(chart, allColors[0].high);
    }

    // Tooltips for the middle buckets
    for (let index = 1; index < (markerCount - 1); index++) {
        rect[index].tooltipText = "≥ " + bocAmchart4.locale(chart, allColors[index].low) + "\n"
        + "< " + bocAmchart4.locale(chart, allColors[index].high);
    }

    // Tooltip for the last bucket
    if (allColors[allColors.length - 1].high === false) {
        rect[markerCount - 1].tooltipText = "≥ " + bocAmchart4.locale(chart, allColors[allColors.length - 1].low);
    } else {
        rect[markerCount - 1].tooltipText = "≥ " + bocAmchart4.locale(chart, allColors[allColors.length - 1].low) +
            "\n≤ " + bocAmchart4.locale(chart, allColors[allColors.length - 1].high);
    }
}

/**
 * Adds optional mid-range labels to the heatmap legend if enabled via chart settings.
 * Labels are only shown on non-mobile screen sizes.
 * @param {object} chart - The amCharts chart instance.
 * @param {object} legendLabelContainer - Container for legend labels.
 * @param {object[]} rect - Array of rectangle elements used for positioning labels.
 * @param {number} markerCount - Number of color rectangles.
 * @returns {void}
 */
function setupMidLabels(chart, legendLabelContainer, rect, markerCount) {
    // Check if mid-labels should be displayed
    if (!chart.bocSettings.args.hasOwnProperty('heatmapdisplaymidlabels') ||
        chart.bocSettings.args.heatmapdisplaymidlabels === 'false') {
        return;
    }

    // Array to hold midColorValue labels
    let midColorValueLabels = [];

    /**
     * Updates mid-range labels on the heatmap legend.
     * This function clears any existing mid-range labels and re-creates them based on screen and chart.
     * Labels are positioned between rectangles if the value matches the lower end of a color bucket.
     * @returns {void}
     */
    function updateMidLabels() {
        const isMobileSize = window.innerWidth < 768;

        // Clear existing midColorValue labels while keeping min and max
        midColorValueLabels.forEach(function (label) {
            label.label.dispose();
        });

        // Reset the array for new labels
        midColorValueLabels = [];

        // Display midColorValue labels when the screen size is big enough
        if (!isMobileSize) {
            const allColors = bocAmchart4.vars[chart.bocSettings.id].allColors;
            const midColorValues = bocAmchart4.vars[chart.bocSettings.id].midColorValuesForTickLabels;

            // Loop through midColorValuesForTickLabels and check if it falls within the rect range
            midColorValues.forEach(function (midColorValue) {
                for (let i = 0; i < markerCount; i++) {
                    // This condition is to check if the midColorValue falls within a bucket
                    // and to display it only if it matches the lower end
                    if ((midColorValue >= allColors[i].low && midColorValue < allColors[i].high) &&
                        midColorValue === allColors[i].low) {
                        const midLabel = legendLabelContainer.createChild(am4core.Label);
                        midLabel.opacity = 0;
                        midLabel.text = bocAmchart4.locale(chart, midColorValue);
                        midLabel.fontSize = "0.875em";
                        midLabel.horizontalCenter = 'middle';
                        midColorValueLabels.push({
                            index: i,
                            label: midLabel
                        });
                    }
                }
            });

            // Position the mid labels
            midColorValueLabels.forEach(function (label) {
                const prevRect = rect[label.index - 1];
                const currentRect = rect[label.index];
                label.label.validate();
                label.label.x = (prevRect.pixelX + currentRect.pixelWidth + currentRect.pixelX) / 2;
                label.label.opacity = 1;
            });
        }
    }
    chart.events.on("layoutvalidated", () => {
        updateMidLabels();
    });

    chart.events.on("sizechanged", updateMidLabels);
}

/**
 * Positions a label between two rectangles by calculating the midpoint.
 * @param {am4core.Label} label - The label to position.
 * @param {object} rectA - The first rectangle.
 * @param {object} rectB - The second rectangle.
 * @returns {void}
 */
function positionLabelBetweenRects(label, rectA, rectB) {
    //return if the labels are invalid
    if (!label || !rectA || !rectB) {
        return;
    }
    //calculate midpoint
    let midpoint = (rectA.pixelX + rectA.pixelWidth + rectB.pixelX) / 2;
    //position labels
    label.x = midpoint - label.measuredWidth / 2;
}

/**
 * Set up the default Amcharts4 heatmap legend with either default/user-defined colours
 * @param {object} chart object
 * @param {object} series target series
 * @param {object} columnTemplate series column template
 * @returns {void}
 */
bocAmchart4.defaultHeatmapLegend = function (chart, series, columnTemplate) {
    const mainColors = bocAmchart4.vars[chart.bocSettings.id].mainColors;

    if (bocAmchart4.debug(chart)) {
        // eslint-disable-next-line no-console
        console.log(
            chart.bocSettings.id,
            "Using these colors:",
            mainColors
        );
    }

    // Set up heat rules for series with either default or user-defined colours
    series.heatRules.push({
        target: columnTemplate,
        property: "fill",
        min: mainColors[0],
        max: mainColors[mainColors.length - 1]
    });

    // Create Amcharts heat legend
    const heatLegend = chart.bottomAxesContainer.createChild(am4charts.HeatLegend);
    heatLegend.width = am4core.percent(100);
    heatLegend.series = series;
    heatLegend.valueAxis.renderer.labels.template.fontSize = "0.563em";
    heatLegend.valueAxis.renderer.minGridDistance = 30;

    // Heat legend behaviour
    series.columns.template.events.on("over", (event) => {
        handleHover(event.target);
    });

    series.columns.template.events.on("hit", (event) => {
        handleHover(event.target);
    });

    /**
     * Show legend tooltip when hovering over a datapoint
     * @param {object} column object
     * @returns {void}
     */
    function handleHover(column) {
        if (!isNaN(column.dataItem.value)) {
            heatLegend.valueAxis.showTooltipAt(column.dataItem.value);
        } else {
            heatLegend.valueAxis.hideTooltip();
        }
    }

    series.columns.template.events.on("out", () => {
        heatLegend.valueAxis.hideTooltip();
    });
};
